import React, { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import { motion, useReducedMotion } from "framer-motion";

import BackToTopFab from "./Components/BackToTopFab";
import Footer from "./Components/Footer";
import Header from "./Components/Header";
import ScrollToTop from "./Components/ScrollToTop";
import HomePage from "./Pages/Homepage";
import ColorModeProvider from "./theme/ColorModeProvider";

// The landing page stays in the main bundle; everything else is split out so
// the first paint doesn't carry the about page's content with it.
const AboutPage = lazy(() => import("./Pages/About"));
const ContactPage = lazy(() => import("./Pages/Contact"));
const NotFound = lazy(() => import("./Pages/NotFound"));
const ProjectsPage = lazy(() => import("./Pages/Projects"));

const PageFallback = () => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CircularProgress aria-label="Loading page" />
  </Box>
);

/** Cross-fades each route so navigation doesn't snap. */
const AnimatedRoutes = () => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const routes = (
    <Routes location={location}>
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/projects" element={<ProjectsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  if (reduceMotion) return routes;

  return (
    <motion.div
      key={location.pathname}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      {routes}
    </motion.div>
  );
};

const App = () => (
  <ColorModeProvider>
    <BrowserRouter>
      <ScrollToTop />

      <Box
        component="a"
        href="#main"
        sx={{
          position: "absolute",
          left: -9999,
          top: 8,
          zIndex: 2000,
          px: 2,
          py: 1,
          borderRadius: 1,
          bgcolor: "background.paper",
          color: "text.primary",
          "&:focus": { left: 8 },
        }}
      >
        Skip to content
      </Box>

      <Box
        sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
      >
        <Header />

        <Box component="main" id="main" sx={{ flex: 1 }}>
          <Suspense fallback={<PageFallback />}>
            <AnimatedRoutes />
          </Suspense>
        </Box>

        <Footer />
      </Box>

      <BackToTopFab />
    </BrowserRouter>
  </ColorModeProvider>
);

export default App;
